import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import {
  platform,
  type GovernedPermissionKey,
  type PlatformGovernance,
} from '@/shared/api/resources';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { useToast } from '@/shared/ui/useToast';

import { permissoesGovernadas } from './labels';

/**
 * Regras e governanca da empresa, no /admin.
 *
 * O dono do produto decidiu, em 18/10/2026, que tres coisas sao decisao do
 * administrador e se editam aqui: quem troca a conta bancaria e o Pix da corretora,
 * quem muda o limite de credito e a situacao do tomador, e se documento
 * obrigatorio pendente impede a emissao. A matriz de papeis da propria empresa
 * continua mostrando as duas permissoes; esta tela e o atalho de quem administra a
 * plataforma, e o servidor confere de novo cada pedido.
 */
export function GovernanceSection({
  tenantId,
  encerrada,
}: {
  tenantId: string;
  encerrada: boolean;
}) {
  const consulta = useQuery({
    queryKey: ['platform', 'governance', tenantId],
    queryFn: ({ signal }) => platform.getGovernance(tenantId, signal),
  });

  return (
    <Card>
      <CardHeader
        title="Regras e governanca"
        description="Decisoes que ficam com a administracao. Cada alteracao fica na trilha da plataforma e na trilha da empresa, com o antes e o depois."
      />
      {consulta.isPending ? (
        <CardBody>
          <p className="text-sm text-ink-500">Carregando as regras da empresa.</p>
        </CardBody>
      ) : consulta.isError ? (
        <CardBody>
          <Alert tone="danger" title="Nao foi possivel carregar as regras">
            {consulta.error.message}
          </Alert>
        </CardBody>
      ) : (
        // A chave refaz o formulario quando o servidor devolve outro estado, para
        // que a tela nunca mostre uma marcacao que ja nao vale.
        <FormularioDeGovernanca
          key={assinatura(consulta.data)}
          tenantId={tenantId}
          governanca={consulta.data}
          encerrada={encerrada}
        />
      )}
    </Card>
  );
}

type Selecao = Record<GovernedPermissionKey, string[]>;

/** O que cada papel editavel tem hoje, por permissao. */
function selecaoInicial(governanca: PlatformGovernance): Selecao {
  const selecao: Selecao = {
    'corretores.dados_bancarios.update': [],
    'tomadores.credito.update': [],
  };
  for (const permissao of governanca.permissions) {
    selecao[permissao.key] = permissao.roles
      .filter((papel) => papel.granted && !papel.locked)
      .map((papel) => papel.role_id);
  }
  return selecao;
}

function assinatura(governanca: PlatformGovernance): string {
  return JSON.stringify([
    governanca.issuance_requires_documents,
    selecaoInicial(governanca),
  ]);
}

function mesmaSelecao(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((item) => b.includes(item));
}

function FormularioDeGovernanca({
  tenantId,
  governanca,
  encerrada,
}: {
  tenantId: string;
  governanca: PlatformGovernance;
  encerrada: boolean;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const inicial = selecaoInicial(governanca);

  const [exigeDocumentos, setExigeDocumentos] = useState(
    governanca.issuance_requires_documents,
  );
  const [selecao, setSelecao] = useState<Selecao>(inicial);

  const salvar = useMutation({
    mutationFn: () =>
      platform.updateGovernance(tenantId, {
        issuance_requires_documents: exigeDocumentos,
        grants: selecao,
      }),
    onSuccess: (atualizada) => {
      queryClient.setQueryData(['platform', 'governance', tenantId], atualizada);
      void queryClient.invalidateQueries({ queryKey: ['platform', 'audit'] });
      toast.success('Regras e governanca atualizadas.');
    },
  });

  const mudou =
    exigeDocumentos !== governanca.issuance_requires_documents ||
    governanca.permissions.some(
      (permissao) => !mesmaSelecao(selecao[permissao.key], inicial[permissao.key]),
    );

  function alternar(
    permissao: GovernedPermissionKey,
    roleId: string,
    marcado: boolean,
  ) {
    setSelecao((atual) => ({
      ...atual,
      [permissao]: marcado
        ? [...atual[permissao], roleId]
        : atual[permissao].filter((item) => item !== roleId),
    }));
  }

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    salvar.mutate();
  }

  return (
    <form onSubmit={enviar}>
      <CardBody className="flex flex-col gap-5">
        {salvar.error ? (
          <Alert tone="danger" title="Nao foi possivel salvar as regras">
            {salvar.error.message}
          </Alert>
        ) : null}

        <fieldset
          disabled={encerrada || salvar.isPending}
          className="flex flex-col gap-5"
        >
          <section className="flex flex-col gap-1.5">
            <h3 className="text-sm font-semibold text-ink-900">Emissao</h3>
            <label className="flex cursor-pointer items-start gap-2 text-sm text-ink-700">
              <input
                type="checkbox"
                role="switch"
                aria-checked={exigeDocumentos}
                className="mt-0.5 size-4 accent-[var(--color-brand-500)]"
                checked={exigeDocumentos}
                onChange={(evento) => setExigeDocumentos(evento.target.checked)}
              />
              <span>Documento obrigatorio pendente impede a emissao</span>
            </label>
            <p className="pl-6 text-2xs text-ink-500">
              Ligada, a emissao so acontece com todo documento obrigatorio da operacao
              recebido ou dispensado, e a recusa diz quais faltam. Desligada, que e o
              padrao, a emissao nao confere documentos.
            </p>
          </section>

          {governanca.permissions.map((permissao) => {
            const texto = permissoesGovernadas[permissao.key];
            return (
              <fieldset
                key={permissao.key}
                className="flex flex-col gap-2 border-t border-line pt-4"
              >
                <legend className="sr-only">{texto.titulo}</legend>
                <h3 className="text-sm font-semibold text-ink-900">{texto.titulo}</h3>
                <p className="text-2xs text-ink-500">
                  {texto.descricao} <span className="font-mono">{permissao.key}</span>
                </p>
                <ul className="grid gap-1.5 sm:grid-cols-2">
                  {permissao.roles.map((papel) => {
                    const marcado = papel.locked
                      ? papel.granted
                      : selecao[permissao.key].includes(papel.role_id);
                    return (
                      <li key={papel.role_id}>
                        <label className="flex cursor-pointer items-center gap-2 text-xs text-ink-700">
                          <input
                            type="checkbox"
                            className="size-4 accent-[var(--color-brand-500)]"
                            checked={marcado}
                            disabled={papel.locked}
                            onChange={(evento) =>
                              alternar(
                                permissao.key,
                                papel.role_id,
                                evento.target.checked,
                              )
                            }
                          />
                          <span className="truncate">{papel.name}</span>
                          {papel.locked ? (
                            <Badge tone="neutral">Sempre tem</Badge>
                          ) : null}
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </fieldset>
            );
          })}
        </fieldset>

        {encerrada ? null : (
          <div className="flex justify-end border-t border-line pt-4">
            <Button type="submit" loading={salvar.isPending} disabled={!mudou}>
              Salvar regras
            </Button>
          </div>
        )}
      </CardBody>
    </form>
  );
}
